import { createRef, useState, forwardRef } from "react";
import { debounce, dropRight, escape, last, lowerCase } from "lodash-es";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import styled from "styled-components";

import { faCheckCircle, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { faCog, faSearch } from "@fortawesome/pro-light-svg-icons";
import { faArrowToBottom } from "@fortawesome/pro-regular-svg-icons";

import {
    SET_BU_REF,
    SET_MERCHANT_REF
} from "common/actions/common";

import { nextTick } from "common/utils";
import { useClickAwayDetector } from "common/hooks";
import { pazienHistory } from "common/components/pazienHistory";
import { Tooltip } from "common/components/tooltip";

import { Webix } from "common/components/webix/react/webix";

import { showExportDialog } from "merchant/features/downloads/exportDialog/exportDialog";

export const BusinessUnitPicker = forwardRef((props, ref) => {
    const { bu_ref, merchant_ref, merchant_config } = useSelector((state) => state.common);
    const { mediaQueryBreakpoint } = useSelector((state) => state.config);
    const { has_bu_export, has_bu_manager } = useSelector((state) => state.common.user_permissions);
    const [treeOpen, setTreeOpen] = useState(false);
    const webixRef = createRef();
    const buPanelRef = createRef();

    const dispatch = useDispatch();

    const getBUpath = (list = merchant_config.business_units) => list.reduce((memo, { id, display_name, children }) => {
        if (id === bu_ref) return [...memo, { id, display_name }];
        if (children.length) {
            const match = getBUpath(children);
            return match.length ? [...memo, { id, display_name }, ...match] : memo;
        }
        return memo;
    }, []);

    const buPath = getBUpath().map(({ display_name }) => display_name);

    const convertCollection = (data) => {
        const selectedId = bu_ref || merchant_ref;
        const convertChildren = (node) => node.children.map((n) => convertNode(n));
        const convertNode = (node) => ({
            id: node.id,
            value: node.display_name,
            open: buPath.includes(node.display_name),
            data: convertChildren(node),
            $css: node.id === selectedId ? "selected" : ""
        });
        return data.map((n) => convertNode(n)).sort((a, b) => (a.value > b.value ? 1 : -1));
    };

    const handleSearch = debounce((e) => {
        const value = lowerCase(e.target.value);
        webixRef.current.ui.filter((obj) => lowerCase(obj.value).includes(value));
    }, 200);

    const handleSelect = (id) => {
        if (id === merchant_ref) {
            dispatch(SET_MERCHANT_REF(id));
        } else {
            dispatch(SET_BU_REF(id));
        }
    };

    const handleExportBUs = () => {
        showExportDialog({
            dialogTitle: "Export hierarchy and MID mappings",
            filters: {},
            filename: `${merchant_config.display_name}_MIDs_${format(new Date(), "yyyy-MM-dd")}`,
            resources: [{ value: "mids", label: "Mids" }],
            activeResource: "mids",
            formats: ["csv", "xlsx"],
            activeFormat: "csv",
            content: [{ value: "mids_mapped", label: "Current MID mappings", structure: null }]
        });
    };

    const goToManager = (e) => {
        e.preventDefault();
        pazienHistory.navigateWithParams("preferences/bumanager", { trigger: true, replace: true });
    };

    const ui = {
        view: "tree",
        select: true,
        width: 700,
        
        template(obj, common) {
            return `${common.icon(obj, common)}<span>${escape(obj.value)}</span>`;
        },
        
        on: {
            onItemClick(id, e, trg) {
                e.stopPropagation();
                e.preventDefault();
                if (e.srcElement === "path") {
                    const node = this.getItem(id);
                    if (node.open) {
                        this.close(id);
                    } else {
                        this.open(id);
                    }
                } else {
                    nextTick(() => handleSelect(id), 0);
                }
            }
        },

        type: {
            icon(obj) {
                const actionClass = obj.open ? "tree_icon webix_tree_open fal fa-minus-square" : "tree_icon webix_tree_close fal fa-plus-square";
                const actionIconClass = obj.$count === 0 ? "tree_icon" : actionClass;
                if (obj.$css === "selected") {
                    return `<span class="${actionIconClass} fas fa-check-circle"></span>`;
                }
                // eslint-disable-next-line sentence-case/sentence-case
                if (obj.value === "Unmapped MIDs") {
                    return `<span class="${actionIconClass} fas fa-question-circle"></span>`;
                }
                return `<span class="${actionIconClass}"></span>`;
            }
            
        }
    };

    useClickAwayDetector(ref, () => setTreeOpen(false));

    return (
        <div ref={ref}>
            <div className="selected-unit" onClick={() => setTreeOpen(!treeOpen)}>
                {bu_ref && <div className="selected-unit-path">{["All levels", ...dropRight(buPath)].join(" > ")}</div>}
                <div className="selected-unit-name">{bu_ref ? last(buPath) : "All levels"}</div>
            </div>
            <div className="expand-unit-menu" onClick={() => setTreeOpen(!treeOpen)}>
                <ChevronIcon icon={faChevronDown} $open={treeOpen}/>
            </div>
            {treeOpen && (
            <BusinessUnitTreePanel ref={buPanelRef} mediaQueryBreakpoint={mediaQueryBreakpoint}>
                <Typeahead>
                    <SearchIcon icon={faSearch} />
                    <TypeaheadInput type="text" className="typeahead" placeholder="Search" onChange={handleSearch}/>
                    {has_bu_export && (
                    <Tooltip title="Export business unit hierarchy and MID mappings" >
                        <ExportIcon icon={faArrowToBottom} size="lg" onClick={handleExportBUs} />
                    </Tooltip>
                    )}
                    {has_bu_manager && (
                    <Tooltip title="Manage business unit hierarchy and MID mappings" >
                        <CogIcon icon={faCog} size="lg" onClick={goToManager} />
                    </Tooltip>
                    )}
                </Typeahead>
                <WebixTreeContainer className="business-units">
                    <AllLevels className={bu_ref ? "" : "selected"} onClick={() => handleSelect(merchant_ref)}>
                        {!bu_ref && <CheckIcon icon={faCheckCircle} />}
                        All levels
                    </AllLevels>
                    <div className="children">
                        <Webix ui={ui} data={convertCollection(merchant_config.business_units)} ref={webixRef} />
                    </div>
                </WebixTreeContainer>
            </BusinessUnitTreePanel>
            )}
        </div>
    );
});

const BusinessUnitTreePanel = styled.div(({ theme: { colors }, mediaQueryBreakpoint }) => `
    position: fixed;
    top: 51px;
    left: ${mediaQueryBreakpoint === "bp-large" ? 180 : 50}px;
    margin: 0;
    padding: 0;
    z-index: 1000;
    border: 1px solid ${colors["gray-light"]};
    border-radius: 0; 
    border-left: 0;
    border-bottom: 0;
    box-shadow: 0 6px 12px ${colors["gray-dark"]};
`);

const WebixTreeContainer = styled.div((props) => `
    display: block;
    width: 700px;
    margin-top: 0;
    border-top: 0;
    border-left: 0;
    border-radius: 0;
    min-height: calc(100vh - 87px);
    max-height: 350px;
    overflow: auto;
    background: ${props.theme.colors["canvas-white"]};
    transition: 0.3s ease-in-out;
`);

const ChevronIcon = styled(FontAwesomeIcon)`
    color: ${(props) => props.theme.colors["gray-light"]};
    transform: ${(props) => (props.$open ? "rotate(180deg)" : "none")};
    transition: 0.3s ease-in-out;

    &:hover {
        color: ${(props) => props.theme.colors["gray-dark"]};
    }
`;

const SearchIcon = styled(FontAwesomeIcon)`
    position: absolute;
    top: 12px;
    left: 8px;
    background: ${(props) => props.theme.colors["canvas-white"]};
`;

const ExportIcon = styled(FontAwesomeIcon)`
    position: absolute;
    top: 8px;
    right: 45px;
    color: ${({ theme }) => theme.colors["paz-black-l-30"]};

    &:hover {
        color: ${(props) => props.theme.colors["blue-wcag-327"]};
    }
`;

const CogIcon = styled(FontAwesomeIcon)`
    position: absolute;
    top: 8px;
    right: 10px;
    color: ${({ theme }) => theme.colors["paz-black-l-30"]};
    
    &:hover {
        color: ${(props) => props.theme.colors["blue-wcag-327"]};
    }
`;

const CheckIcon = styled(FontAwesomeIcon)`
    color: ${(props) => props.theme.colors["green-leaf"]};
    height: 16px;
    width: 16px;
    margin: 0 10px 0 6px;
`;

const AllLevels = styled.div`
    padding: 5px 10px 0;
    color: ${(props) => props.theme.colors["gray-dark"]};
    font-family: ${({ theme }) => theme.text.regular};
    font-size: ${({ theme }) => theme.fontSizeLarge};

    &.selected {
        color: ${(props) => props.theme.colors["paz-black"]};
        font-family: ${({ theme }) => theme.text.bold};
        font-size: ${({ theme }) => theme.fontSizeExtraLarge};
    }
`;

const Typeahead = styled.div(({ theme: { colors } }) => `
    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    justify-content: stretch;
    align-items: center;
    position: relative;
`);

const TypeaheadInput = styled.input(({ theme }) => `
    display: flex;
    flex: 1 1 auto;
    color: ${theme.colors["gray-dark"]};
    font-family: ${theme.text.regular};
    font-size: ${theme.fontSizeExtraLarge};
    border: 0;
    border-bottom: 1px solid ${theme.colors["gray-light"]};
    background: ${theme.colors["canvas-white"]};
    padding: 10px 50px 10px 30px;
`);
